import { type FC, useMemo } from 'react'
import {
	AlertFillCircleIcon,
	AlertTriangleFillIcon,
	CheckCircleFillIcon
} from '@/components/ui/icons'
import { cn } from '@/helpers/utils'
import { type TKey, useTranslation } from '@/translations/i18n'
import type { VettingQuestion } from '../types'
import {
	FAIL_VALUES,
	HARD_STAKE_CODES,
	SOFT_STAKE_CODES,
	UNSURE_VALUES
} from '../utils'

const BANNER_CLASSNAME =
	'flex gap-3 p-4 [&_p]:font-semibold bg-grey-100 text-sm items-center text-neutral-900'

interface StakeInfo {
	code: string
	questionId: string
	answer: string
}

interface TableStakeBannerProps {
	formAnswers: Record<string, string>
	questions: VettingQuestion[]
	className?: string
	hideActionPrompts?: boolean
}

export const TableStakeBanner: FC<TableStakeBannerProps> = ({
	formAnswers,
	questions,
	className,
	hideActionPrompts = false
}) => {
	const { t } = useTranslation()

	const tableStakeQuestions = useMemo(
		() => questions.filter((q) => q.tier === 'table_stake'),
		[questions]
	)

	const hardStakes = useMemo(
		() => tableStakeQuestions.filter((q) => HARD_STAKE_CODES.has(q.code)),
		[tableStakeQuestions]
	)

	const softStakes = useMemo(
		() => tableStakeQuestions.filter((q) => SOFT_STAKE_CODES.has(q.code)),
		[tableStakeQuestions]
	)

	const getStakeInfo = (
		stakes: VettingQuestion[],
		filter: (value: string) => boolean
	): StakeInfo[] => {
		const result: StakeInfo[] = []
		for (const q of stakes) {
			const answer = formAnswers[q.id]
			if (answer && filter(answer)) {
				result.push({ code: q.code, questionId: q.id, answer })
			}
		}
		return result
	}

	const hardFailed = getStakeInfo(hardStakes, (v) => FAIL_VALUES.has(v))
	const hardUnsure = getStakeInfo(hardStakes, (v) => UNSURE_VALUES.has(v))
	const softFlagged = getStakeInfo(
		softStakes,
		(v) => FAIL_VALUES.has(v) || UNSURE_VALUES.has(v)
	)

	const allStakeIds = [...hardStakes, ...softStakes].map((q) => q.id)
	const allAnswered = allStakeIds.every((id) => formAnswers[id])
	const hasAnyIssue =
		hardFailed.length > 0 || hardUnsure.length > 0 || softFlagged.length > 0

	if (!(allAnswered || hasAnyIssue)) {
		return null
	}

	const allHardFailed = hardFailed.length === hardStakes.length
	const isMixed = hardFailed.length > 0 && hardUnsure.length > 0

	const codeLower = (code: string) => code.toLowerCase()

	const renderHardFailContent = () => {
		if (allHardFailed) {
			return (
				<p>{t('luxe_submissions:score_form.banner_all_hard_failed')}</p>
			)
		}

		if (isMixed) {
			const stakesText = hardFailed
				.map((s) =>
					t(
						`luxe_submissions:score_form.${codeLower(s.code)}_short_label` as TKey
					)
				)
				.join(', ')
			return (
				<p>
					{t('luxe_submissions:score_form.banner_hard_failed', {
						stakes: stakesText,
						count: hardFailed.length
					})}
				</p>
			)
		}

		return (
			<>
				<p>{t('luxe_submissions:score_form.breached_table_stakes')}</p>
				<ul className='ml-6 list-disc font-semibold'>
					{hardFailed.map((stake) => (
						<li key={stake.code}>
							{t(
								`luxe_submissions:score_form.${codeLower(stake.code)}_short_label` as TKey
							)}
							:{' '}
							{t(
								`luxe_submissions:score_form.${codeLower(stake.code)}_long_label` as TKey
							)}{' '}
							({t('luxe_submissions:score_form.no')})
						</li>
					))}
				</ul>
			</>
		)
	}

	return (
		<div className={cn('flex flex-col gap-4', className)}>
			{/* Hard fail banner */}
			{hardFailed.length > 0 && (
				<div className={BANNER_CLASSNAME}>
					<AlertFillCircleIcon className='size-5 shrink-0 text-red-700' />
					<div className='flex flex-col'>
						{renderHardFailContent()}
						{!hideActionPrompts && (
							<p>
								{t(
									'luxe_submissions:score_form.banner_continue_rating'
								)}
							</p>
						)}
					</div>
				</div>
			)}

			{/* Unsure / Alternative banner */}
			{hardUnsure.length > 0 && (
				<div className={BANNER_CLASSNAME}>
					<AlertTriangleFillIcon className='size-5 shrink-0 text-orange-600' />
					<div className='flex flex-col'>
						<p>
							{t('luxe_submissions:score_form.banner_unsure', {
								stakes: hardUnsure
									.map(
										(s) =>
											`${t(`luxe_submissions:score_form.${codeLower(s.code)}_short_label` as TKey)}`
									)
									.join(', '),
								count: hardUnsure.length
							})}

							{!hideActionPrompts &&
								t(
									'luxe_submissions:score_form.banner_unsure_action'
								)}
						</p>
						{!hideActionPrompts && hardFailed.length === 0 && (
							<p>
								{t(
									'luxe_submissions:score_form.banner_continue_rating'
								)}
							</p>
						)}
					</div>
				</div>
			)}

			{/* Soft flag banner */}
			{softFlagged.length > 0 && (
				<div className={BANNER_CLASSNAME}>
					<AlertFillCircleIcon className='size-5 shrink-0 text-grey-700' />
					<div className='flex flex-col'>
						<ul className='list-none font-semibold'>
							{softFlagged.map((stake) => (
								<li key={stake.code}>
									{t(
										`luxe_submissions:score_form.${codeLower(stake.code)}_short_label` as TKey
									)}
									:{' '}
									{t(
										`luxe_submissions:score_form.${codeLower(stake.code)}_options.${stake.answer}` as TKey
									)}
								</li>
							))}
						</ul>
						<p>
							{t('luxe_submissions:score_form.banner_soft_flag')}
						</p>
						{!hideActionPrompts && (
							<p>
								{t(
									'luxe_submissions:score_form.banner_continue_rating'
								)}
							</p>
						)}
					</div>
				</div>
			)}

			{/* All clear banner */}
			{allAnswered && !hasAnyIssue && (
				<div className={BANNER_CLASSNAME}>
					<CheckCircleFillIcon className='size-5 shrink-0 text-green-600' />

					<p>
						{t('luxe_submissions:score_form.banner_all_passed')}{' '}
						{!hideActionPrompts &&
							t(
								'luxe_submissions:score_form.banner_all_passed_action'
							)}
					</p>
				</div>
			)}
		</div>
	)
}
